import React from 'react';
import { IntlProvider } from 'react-intl';
import { OptionalIntlConfig } from 'react-intl/dist/components/provider';
import { useSelector } from 'react-redux';

import { reduce } from 'lodash';
import moment from 'moment';

import { Language } from '../../resources/types';
import { selectLocale } from '../../selectors';

export const languages = [Language.English, Language.Spanish, Language.Portuguese];

export enum Country {
  US = 'US',
  Brazil = 'BR',
}
export const countries: Country[] = [Country.US, Country.Brazil];

export enum Locale {
  EnglishUS = 'en-US',
  SpanishUS = 'es-US',
  PortugueseBR = 'pt-BR',
}
export const locales: Locale[] = [Locale.EnglishUS, Locale.SpanishUS, Locale.PortugueseBR];

const DEFAULT_LOCALE = Locale.EnglishUS;

/**
 * Setup the i18n language files and locale data for your app.
 *
 * TODO: Introduce `extract-intl` script.
 * More info: https://github.com/react-boilerplate/react-boilerplate/blob/master/app/i18n.js
 *
 * @param locale
 * @param messages
 * @param defaultMessages
 */
const formatTranslationMessages = (locale: string, messages: any, defaultMessages?: any) => {
  const defaultFormattedMessages =
    locale !== DEFAULT_LOCALE ? formatTranslationMessages(DEFAULT_LOCALE, defaultMessages) : ({} as any);
  return Object.keys(messages).reduce((formattedMessages, key) => {
    let message = messages[key];
    if (!message && locale !== DEFAULT_LOCALE) {
      message = defaultFormattedMessages[key];
    }
    return Object.assign(formattedMessages, { [key]: message });
  }, {});
};

type Messages = Record<Locale, { [k: string]: string }>;

export interface LanguageProviderProps extends Omit<OptionalIntlConfig, 'locale' | 'messages'> {
  messages: Messages;
}

/**
 * Connects the redux state language locale to the
 * IntlProvider component and i18n messages (loaded from `src/i18n`)
 */
const LanguageProvider: React.FC<LanguageProviderProps> = ({ messages: messagesObj, children, ...rest }) => {
  const locale = useSelector(selectLocale);

  const defaultMessages = messagesObj[DEFAULT_LOCALE];
  const messages = reduce(
    messagesObj,
    (result, val, key) => ({
      ...result,
      [key]: formatTranslationMessages(key, val, defaultMessages),
    }),
    {} as Messages,
  );

  if (locale) {
    moment.locale(locale);
  }

  return (
    <IntlProvider locale={locale} messages={messages[locale]} {...rest}>
      {React.Children.only(children)}
    </IntlProvider>
  );
};

export default LanguageProvider;
